import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import {isSuperAdmin} from "../../system/admin-access";
import {
    fetchAdminRoles,
    fetchSupportRoleAccess,
    updateSupportRoleAccess,
} from "../../system/support-access-api";
import {useTranslate} from "../../system/useTranslate";

function errorText(error) {
    return error?.response?.data?.error?.message
        || error?.response?.data?.error
        || error?.message
        || "admin-access-request-error";
}

function permissionLabelKey(code) {
    return `support-permission-${String(code).replace(/\./g, "-")}`;
}

function AdminAccess() {
    const translate = useTranslate();
    const user = useSelector((state) => state.auth.user);
    const [roles, setRoles] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [roleAccess, setRoleAccess] = useState({});
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [nextRoles, access] = await Promise.all([
                fetchAdminRoles(),
                fetchSupportRoleAccess(),
            ]);
            setRoles(nextRoles);
            setCatalog(access.catalog || []);
            setRoleAccess(access.roleAccess || {});
            setSelectedRole((current) => (
                nextRoles.find((role) => String(role.id) === String(current?.id))
                || nextRoles.find((role) => role.code !== "strapi-super-admin")
                || nextRoles[0]
                || null
            ));
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isSuperAdmin(user)) load();
    }, [load, user]);

    useEffect(() => {
        if (!selectedRole) {
            setSelectedPermissions([]);
            return;
        }
        setSelectedPermissions(
            selectedRole.code === "strapi-super-admin"
                ? catalog.map(({code}) => code)
                : roleAccess[String(selectedRole.id)] || []
        );
        setSuccess(null);
    }, [catalog, roleAccess, selectedRole]);

    const groupedPermissions = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        const groups = new Map();
        catalog.forEach((permission) => {
            const translated = translate(permissionLabelKey(permission.code));
            if (
                normalizedQuery
                && !permission.code.toLowerCase().includes(normalizedQuery)
                && !translated.toLowerCase().includes(normalizedQuery)
            ) return;
            if (!groups.has(permission.group)) groups.set(permission.group, []);
            groups.get(permission.group).push(permission);
        });
        return [...groups.entries()];
    }, [catalog, query, translate]);

    const selectedSet = useMemo(() => new Set(selectedPermissions), [selectedPermissions]);
    const protectedRole = selectedRole?.code === "strapi-super-admin";

    const togglePermission = (code, checked) => {
        if (protectedRole) return;
        setSelectedPermissions((current) => (
            checked
                ? [...new Set([...current, code])]
                : current.filter((permission) => permission !== code)
        ));
    };

    const toggleGroup = (permissions, checked) => {
        if (protectedRole) return;
        const groupCodes = new Set(permissions.map(({code}) => code));
        setSelectedPermissions((current) => (
            checked
                ? [...new Set([...current, ...groupCodes])]
                : current.filter((permission) => !groupCodes.has(permission))
        ));
    };

    const save = async () => {
        if (!selectedRole || protectedRole) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await updateSupportRoleAccess(selectedRole.id, selectedPermissions);
            setRoleAccess((current) => ({
                ...current,
                [String(selectedRole.id)]: selectedPermissions,
            }));
            setSuccess("support-access-saved");
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    if (!isSuperAdmin(user)) {
        return (
            <AdminAccessDenied
                titleKey="admin-access-denied-title"
                descriptionKey="admin-access-denied-description"
            />
        );
    }

    return (
        <div className="w-full h-full overflow-auto flex flex-column gap-3 p-3 rtl text-xs">
            <PageHeaderButton
                title={translate("admin-access-title")}
                description={translate("support-access-description")}
                end={(
                    <Button
                        icon="pi pi-refresh"
                        label={translate("refresh")}
                        className="p-button-sm p-button-outlined gap-2"
                        loading={loading}
                        onClick={load}
                    />
                )}
            />

            <Message severity="info" text={translate("support-access-isolated-notice")} className="text-xs"/>
            {error && <Message severity="error" text={translate(error)} className="text-xs"/>}
            {success && <Message severity="success" text={translate(success)} className="text-xs"/>}

            <div className="grid m-0 flex-1">
                <div className="col-12 lg:col-4">
                    <div className="surface-card border-1 border-200 border-round h-full overflow-hidden">
                        <div className="p-3 border-bottom-1 border-200">
                            <span className="font-bold text-sm">{translate("support-access-existing-roles")}</span>
                        </div>
                        <DataTable
                            value={roles}
                            dataKey="id"
                            loading={loading}
                            selectionMode="single"
                            selection={selectedRole}
                            onSelectionChange={(event) => setSelectedRole(event.value)}
                            rowHover
                            scrollable
                            scrollHeight="flex"
                            emptyMessage={translate("datatable-empty-message")}
                            className="h-full"
                        >
                            <Column field="name" header={translate("role")} bodyClassName="text-right" headerClassName="text-right"/>
                            <Column
                                header={translate("support-access-count")}
                                body={(role) => (
                                    <Tag
                                        severity={role.code === "strapi-super-admin" ? "info" : "secondary"}
                                        value={role.code === "strapi-super-admin"
                                            ? translate("all")
                                            : (roleAccess[String(role.id)] || []).length}
                                    />
                                )}
                                bodyClassName="text-right"
                                headerClassName="text-right"
                            />
                        </DataTable>
                    </div>
                </div>

                <div className="col-12 lg:col-8">
                    <div className="surface-card border-1 border-200 border-round h-full flex flex-column overflow-hidden">
                        <div className="p-3 border-bottom-1 border-200 flex flex-wrap align-items-center justify-content-between gap-3">
                            <div className="flex flex-column gap-1">
                                <span className="font-bold text-sm">{selectedRole?.name || "-"}</span>
                                <span className="text-xs text-500">{translate("support-access-role-help")}</span>
                            </div>
                            <Tag
                                severity={protectedRole ? "info" : "warning"}
                                value={`${selectedPermissions.length} ${translate("support-access-selected")}`}
                            />
                        </div>
                        <div className="p-3 border-bottom-1 border-200">
                            <span className="p-input-icon-right w-full">
                                <i className="pi pi-search"/>
                                <InputText
                                    className="w-full"
                                    value={query}
                                    placeholder={translate("support-access-search")}
                                    onChange={(event) => setQuery(event.target.value)}
                                />
                            </span>
                        </div>
                        <div className="p-3 flex-1 overflow-auto flex flex-column gap-3">
                            {protectedRole && (
                                <Message
                                    severity="info"
                                    text={translate("support-access-super-admin-notice")}
                                    className="text-xs"
                                />
                            )}
                            {groupedPermissions.map(([group, permissions]) => {
                                const selectedCount = permissions.filter(({code}) => selectedSet.has(code)).length;
                                const allSelected = permissions.length > 0 && selectedCount === permissions.length;
                                return (
                                    <section key={group} className="border-1 border-200 border-round">
                                        <div className="p-3 border-bottom-1 border-200 flex align-items-center justify-content-between">
                                            <div className="flex flex-column gap-1">
                                                <span className="font-bold">{translate(`support-permission-group-${group}`)}</span>
                                                <span className="text-xs text-500 ltr">{selectedCount} / {permissions.length}</span>
                                            </div>
                                            <div className="flex align-items-center gap-2">
                                                <label htmlFor={`support-group-${group}`}>{translate("select-all")}</label>
                                                <Checkbox
                                                    inputId={`support-group-${group}`}
                                                    checked={allSelected}
                                                    disabled={protectedRole}
                                                    onChange={(event) => toggleGroup(permissions, event.checked)}
                                                />
                                            </div>
                                        </div>
                                        <div className="grid p-2">
                                            {permissions.map(({code}) => (
                                                <div key={code} className="col-12 md:col-6">
                                                    <label
                                                        htmlFor={`support-permission-${code}`}
                                                        className="p-3 border-1 border-200 border-round flex align-items-start gap-2 h-full cursor-pointer"
                                                    >
                                                        <Checkbox
                                                            inputId={`support-permission-${code}`}
                                                            checked={selectedSet.has(code)}
                                                            disabled={protectedRole}
                                                            onChange={(event) => togglePermission(code, event.checked)}
                                                        />
                                                        <span className="flex flex-column gap-1">
                                                            <span className="font-medium">{translate(permissionLabelKey(code))}</span>
                                                            <span className="text-2xs text-500 ltr">{code}</span>
                                                        </span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                        {!protectedRole && (
                            <div className="p-3 surface-50 border-top-1 border-200 flex flex-row-reverse gap-2">
                                <Button
                                    icon="pi pi-save"
                                    label={translate("save-changes")}
                                    className="p-button-sm gap-2"
                                    loading={saving}
                                    onClick={save}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminAccess;
